import { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { getLiveTrainingPortalRoot } from "../../hooks/useLiveFullscreen";
import { Gauge, User, X } from "lucide-react";
import { useTranslation } from "react-i18next";
import VideocamIcon from "@mui/icons-material/Videocam";
import {
  isWebEvalCameraSelected,
  webEvalCameraButtonLabel,
  webEvalCameraLensMark,
  webEvalCameraSetupLabel,
} from "../../utils/evalVideoEncoding";
import {
  COACH_CALIBRATION_FPS,
  COACH_CALIBRATION_RESOLUTIONS,
  normalizeCoachCalibrationSettings,
  shouldRefreshCalibrationFrame,
} from "../../utils/coachCameraCalibration";
import "./LiveTrainingVideoCalibrationOverlay.css";
import "./LiveTrainingCoachCameraCalibration.css";

function newRequestId() {
  return `calib-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function errorText(code, t) {
  if (code === "recording") {
    return t(
      "liveTrainingCoachCameraRecording",
      "That camera is recording. Stop the clip before changing FPS, resolution, or lens."
    );
  }
  if (code === "camera_unavailable") {
    return t(
      "liveTrainingCoachCameraUnavailable",
      "Camera unavailable on the player phone."
    );
  }
  return t(
    "liveTrainingCoachCameraNoFrame",
    "No frame yet. The camera phone did not answer."
  );
}

/**
 * Coach review of one player-camera frame. FPS, resolution, and lens are
 * applied on that phone. The still updates only after one of those changes.
 */
export function LiveTrainingCoachCameraCalibration({
  open = false,
  tableNumber = null,
  socket = null,
  tsId = null,
  onClose,
}) {
  const { t } = useTranslation();
  const [frameUrl, setFrameUrl] = useState("");
  const [cameras, setCameras] = useState([]);
  const [applied, setApplied] = useState(null);
  const [picture, setPicture] = useState(null);
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");
  const requestRef = useRef("");
  const appliedRef = useRef(null);
  const pendingRef = useRef(null);
  const frameUrlRef = useRef("");
  const timeoutRef = useRef(0);
  appliedRef.current = applied;
  frameUrlRef.current = frameUrl;

  const clearWait = () => window.clearTimeout(timeoutRef.current);

  const requestFrame = useCallback(
    (reason, settings) => {
      const table = Number(tableNumber);
      if (!socket || !tsId || !Number.isFinite(table) || table <= 0) return;
      const requestId = newRequestId();
      requestRef.current = requestId;
      setError("");
      setStatus(reason === "open" && !frameUrlRef.current ? "waiting" : "updating");
      clearWait();
      timeoutRef.current = window.setTimeout(() => {
        if (requestRef.current !== requestId) return;
        pendingRef.current = null;
        setError(
          t(
            "liveTrainingCoachCameraNoFrame",
            "No frame yet. The camera phone did not answer."
          )
        );
        setStatus(frameUrlRef.current ? "ready" : "error");
      }, 12000);
      socket.emit(
        "requestPlayerCameraCalibration",
        {
          ts_id: String(tsId),
          table_number: table,
          request_id: requestId,
          reason,
          settings: reason === "settings" ? settings : null,
        },
        (ack) => {
          if (requestRef.current !== requestId) return;
          if (ack && ack.ok === false) {
            clearWait();
            pendingRef.current = null;
            setError(errorText(ack.code, t));
            setStatus(frameUrlRef.current ? "ready" : "error");
          }
        }
      );
    },
    [socket, t, tableNumber, tsId]
  );

  useEffect(() => {
    if (!open) return undefined;
    setFrameUrl("");
    setCameras([]);
    setApplied(null);
    setPicture(null);
    setError("");
    pendingRef.current = null;
    frameUrlRef.current = "";
    requestFrame("open");
    return () => {
      window.clearTimeout(timeoutRef.current);
    };
    // Fresh snapshot each time the coach opens this table.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, tableNumber, tsId]);

  useEffect(() => {
    if (!socket || !open) return undefined;
    const onFrame = (payload = {}) => {
      if (String(payload.request_id || "") !== requestRef.current) return;
      window.clearTimeout(timeoutRef.current);
      if (payload.unchanged === true) {
        pendingRef.current = null;
        setStatus("ready");
        return;
      }
      if (payload.ok === false || !payload.frame_jpeg) {
        pendingRef.current = null;
        setError(errorText(payload.code, t));
        setStatus(frameUrlRef.current ? "ready" : "error");
        return;
      }
      pendingRef.current = null;
      const nextApplied = normalizeCoachCalibrationSettings(payload.applied);
      setApplied(nextApplied);
      setCameras(Array.isArray(payload.cameras) ? payload.cameras : []);
      setPicture({
        width: Number(payload.width) || 0,
        height: Number(payload.height) || 0,
        fps: Number(payload.fps) || nextApplied.fps || null,
      });
      setFrameUrl(`data:image/jpeg;base64,${payload.frame_jpeg}`);
      setError("");
      setStatus("ready");
    };
    socket.on("playerCameraCalibrationFrame", onFrame);
    return () => socket.off("playerCameraCalibrationFrame", onFrame);
  }, [socket, open, t]);

  const close = () => {
    const table = Number(tableNumber);
    if (socket && tsId && Number.isFinite(table) && table > 0) {
      socket.emit("releasePlayerCameraCalibration", {
        ts_id: String(tsId),
        table_number: table,
      });
    }
    requestRef.current = "";
    pendingRef.current = null;
    window.clearTimeout(timeoutRef.current);
    onClose?.();
  };

  const pushSettings = (patch) => {
    const baseline = pendingRef.current || appliedRef.current;
    const next = normalizeCoachCalibrationSettings({
      ...(baseline || {}),
      ...patch,
    });
    if (!shouldRefreshCalibrationFrame(baseline, next)) return;
    pendingRef.current = next;
    requestFrame("settings", {
      fps: next.fps,
      resolution: next.resolution,
      device_id: next.deviceId,
      facing_mode: next.facingMode,
      lens: next.lens,
    });
  };

  if (!open) return null;

  const portalRoot =
    typeof document !== "undefined" ? getLiveTrainingPortalRoot() : null;
  if (!portalRoot) return null;

  const busy = status === "waiting" || status === "updating";
  const tableLabel =
    t("table_with_number", { number: tableNumber ?? "—" }) ||
    `Table ${tableNumber ?? "—"}`;
  const hint = error
    ? error
    : status === "waiting"
      ? t(
          "liveTrainingCoachCameraFrameWaiting",
          "Waiting for the player camera…"
        )
      : status === "updating"
        ? t("liveTrainingCoachCameraFrameUpdating", "Updating frame…")
        : t(
            "liveTrainingCoachCameraCalibrationHint",
            "One frame from the player camera. It updates only when you change FPS, resolution, or lens."
          );

  return createPortal(
    <div
      className="lt-eval-calib lt-coach-calib"
      role="dialog"
      aria-modal="true"
      aria-labelledby="lt-coach-calib-title"
    >
      <div
        className={`lt-eval-calib__stage${
          status === "updating" ? " lt-eval-calib__stage--switching" : ""
        }`}
      >
        {frameUrl ? (
          <img
            className="lt-eval-calib__preview"
            src={frameUrl}
            alt={t("liveTrainingCoachCameraCalibration", "Player camera")}
          />
        ) : (
          <div className="lt-coach-calib__wait">{hint}</div>
        )}
      </div>

      <header className="lt-eval-calib__top">
        <div className="lt-eval-calib__title-row">
          <VideocamIcon fontSize="small" />
          <h2 id="lt-coach-calib-title" className="lt-eval-calib__title">
            {t("liveTrainingCoachCameraCalibration", "Player camera")}
          </h2>
          <span className="lt-eval-calib__meta-chip">{tableLabel}</span>
          {picture?.width && picture?.height ? (
            <span className="lt-eval-calib__meta-chip">
              {t("liveTrainingCoachCameraActual", {
                width: picture.width,
                height: picture.height,
                fps: picture.fps || "—",
                defaultValue: `${picture.width}×${picture.height} · ${picture.fps || "—"} FPS`,
              })}
            </span>
          ) : null}
        </div>
        <button
          type="button"
          className="lt-eval-calib__icon-btn"
          onClick={close}
          aria-label={t("close", "Close")}
          title={t("close", "Close")}
        >
          <X size={20} strokeWidth={2.2} />
        </button>
      </header>

      <p className="lt-eval-calib__hint">{frameUrl ? hint : null}</p>

      <div
        className="lt-eval-calib__chrome"
        aria-label={t("liveTrainingCoachCameraCalibration", "Player camera")}
      >
        <div
          className="lt-eval-calib__edge lt-eval-calib__edge--right"
          role="group"
          aria-label={t("liveTrainingEvalCamera", "Camera")}
        >
          <div className="lt-eval-calib__rail">
            <span className="lt-eval-calib__rail-label">
              {t("liveTrainingEvalCamera", "Lens")}
            </span>
            <div className="lt-eval-calib__dock-row lt-eval-calib__dock-row--lenses">
              {cameras.map((option) => {
                const selected = isWebEvalCameraSelected(option, {
                  deviceId: applied?.deviceId,
                  facingMode: applied?.facingMode,
                });
                const label = webEvalCameraButtonLabel(option, cameras, t);
                const mark = webEvalCameraLensMark(option);
                const isWide = option.lens === "ultrawide";
                return (
                  <button
                    key={option.id || option.deviceId || label}
                    type="button"
                    className={`lt-eval-calib__lens${selected ? " is-on" : ""}${
                      isWide ? " lt-eval-calib__lens--wide" : ""
                    }`}
                    onClick={() =>
                      pushSettings({
                        deviceId: option.deviceId,
                        facingMode: option.facingMode,
                        lens: option.lens,
                      })
                    }
                    aria-pressed={selected}
                    disabled={busy || !applied}
                    title={label}
                    aria-label={label}
                  >
                    <span className="lt-eval-calib__lens-mark">
                      {mark === "F" && !isWide ? (
                        <User size={16} strokeWidth={2.2} aria-hidden />
                      ) : (
                        mark
                      )}
                    </span>
                    <span className="lt-eval-calib__lens-name">
                      {webEvalCameraSetupLabel(option, t)}
                    </span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="lt-eval-calib__rail">
            <span className="lt-eval-calib__rail-label">
              <Gauge size={10} strokeWidth={2.4} aria-hidden />
              {t("liveTrainingEvalFps", "FPS")}
            </span>
            <div
              className="lt-eval-calib__dock-row lt-eval-calib__dock-row--stack"
              role="group"
              aria-label={t("liveTrainingEvalFps", "FPS")}
            >
              {COACH_CALIBRATION_FPS.map((rate) => {
                const active = applied?.fps === rate;
                return (
                  <button
                    key={rate}
                    type="button"
                    className={`lt-eval-calib__chip${active ? " is-on" : ""}`}
                    onClick={() => pushSettings({ fps: rate })}
                    aria-pressed={active}
                    disabled={busy || !applied}
                    title={`${rate} FPS`}
                    aria-label={`${rate} FPS`}
                  >
                    <span>{rate}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="lt-eval-calib__rail">
            <span className="lt-eval-calib__rail-label">
              {t("liveTrainingRecordingResolution", "Res")}
            </span>
            <div
              className="lt-eval-calib__dock-row lt-eval-calib__dock-row--stack"
              role="group"
              aria-label={t("liveTrainingRecordingResolution", "Resolution")}
            >
              {COACH_CALIBRATION_RESOLUTIONS.map((resolution) => {
                const active = applied?.resolution === resolution;
                return (
                  <button
                    key={resolution}
                    type="button"
                    className={`lt-eval-calib__chip${active ? " is-on" : ""}`}
                    onClick={() => pushSettings({ resolution })}
                    aria-pressed={active}
                    disabled={busy || !applied}
                    title={resolution}
                    aria-label={resolution}
                  >
                    <span>{resolution}</span>
                  </button>
                );
              })}
            </div>
          </div>

          {status === "error" ? (
            <div className="lt-eval-calib__rail">
              <button
                type="button"
                className="lt-eval-calib__chip is-on"
                onClick={() => requestFrame("open")}
              >
                {t("liveTrainingCoachCameraRetry", "Try again")}
              </button>
            </div>
          ) : null}
        </div>
      </div>
    </div>,
    portalRoot
  );
}

export default LiveTrainingCoachCameraCalibration;
